import { useState, useRef, useMemo } from 'react';
import * as turf from '@turf/turf';
import { v4 as uuidv4 } from 'uuid';
import { ITrainersData, ITrainersTool } from '@/types/trainers';

import SelectableMap, {
  MapUse,
} from '@/components/ui/trainers/shared/SelectableMap';
import { Views } from '@/types/view';
import { ScoutProvider } from '@/hooks/useScout';
import { ScoutLocation } from '@/types/location';
import { SelectedFeature } from '@/types/scout';
import { getNormalizedPoints, getNormalizedImages } from '@/utils/trainers';
import { ResponseType } from '@/components/admin/trainers/viewers/AdminWrapperObjectViewer';
import ImageCarousel from '../ImageCarousel';

const MAP_BUFFER_METERS = 50;

interface Props {
  data?: ITrainersData;
  tool?: ITrainersTool;
  setSelectedSegments?: (segments: SelectedFeature[]) => void;
  actualResponse?: ResponseType;
  expectedResponse?: ResponseType;
}

export default function RegionPickerTrainerContent({
  data,
  tool,
  setSelectedSegments,
  actualResponse,
  expectedResponse,
}: Props) {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const {
    leaseId = 'noleaseid',
    frames,
    closeFrames,
    images: contentImages = [],
  } = data?.content || {};

  const bufferedMapLocation: ScoutLocation | undefined = useMemo(() => {
    if (data?.content?.imagesCoords) {
      const imageCoords = data.content.imagesCoords;
      const formatted = imageCoords.map(coord => {
        return [coord.lon, coord.lat];
      });

      const pointsFeatureCollection = turf.featureCollection(
        formatted.map(coord => turf.point(coord)),
      );

      const bbox = turf.bbox(pointsFeatureCollection);
      const bboxPolygon = turf.bboxPolygon(bbox);
      const bufferedPolygon = turf.buffer(bboxPolygon, MAP_BUFFER_METERS, {
        units: 'meters',
      });

      if (!bufferedPolygon) {
        return undefined;
      }

      const scoutLoc: ScoutLocation = {
        _id: uuidv4(),
        geojson: bufferedPolygon.geometry,
        name: 'Buffered Map',
      };

      return scoutLoc;
    }
  }, [data]);

  const normalizedPoints = useMemo(
    () =>
      data
        ? getNormalizedPoints(
            data?.content?.frames,
            data?.content?.closeFrames,
            data?.content?.imagesCoords,
          )
        : [],
    [data],
  );

  const normalizedImages = useMemo(
    () => getNormalizedImages(frames, closeFrames, contentImages),
    [frames, contentImages, closeFrames],
  );

  function parseSegmentIds(response: ResponseType | undefined) {
    if (!response) {
      return [];
    }

    try {
      const responseValue = response?.value;
      const parsed = JSON.parse(responseValue as string);
      const segmentIds = Object.entries(parsed)
        .map(([segmentId, intervals]) => {
          return (intervals as [number, number][]).map(
            (interval: [number, number]) => {
              return `${segmentId}-${interval.join('-')}`;
            },
          );
        })
        .flat();

      return segmentIds;
    } catch (e) {
      console.error(`Error parsing segment ids: ${e}`);
      return [];
    }
  }

  const selectedSegmentIds = useMemo(() => {
    if (actualResponse) {
      return parseSegmentIds(actualResponse);
    }

    if (!actualResponse && expectedResponse) {
      return parseSegmentIds(expectedResponse);
    }

    if (data?.content?.attrs) {
      return data.content.attrs.map(attr => {
        const buffer = Buffer.from(attr.entityId.data);
        const stringId = buffer.toString('hex');

        const segmentId = `${stringId}-${attr.entitySection?.join('-')}`;
        return segmentId;
      });
    } else {
      return undefined;
    }
  }, [data, actualResponse, expectedResponse]);

  const expectedSelectedSegmentIds = useMemo(() => {
    if (expectedResponse) {
      return parseSegmentIds(expectedResponse);
    }

    return undefined;
  }, [expectedResponse]);

  return (
    <div className="flex h-4/5 flex-row items-center justify-center">
      <div className="mt-2 flex h-full w-1/2 flex-col items-start">
        <ImageCarousel
          key={leaseId}
          images={normalizedImages}
          onSelectedImageChange={setSelectedImageIndex}
        />
      </div>
      <div className="flex h-4/5 w-3/5 flex-col">
        {bufferedMapLocation && (
          <ScoutProvider locations={[bufferedMapLocation]}>
            <SelectableMap
              activeView={Views.Map}
              locations={[]}
              initialLocations={[bufferedMapLocation]}
              segmentSelectionCallback={setSelectedSegments}
              mapUse={MapUse.TRAINER}
              selectedSegmentIds={selectedSegmentIds}
              preventClick={tool?.queue !== 'eventRegionPicker'}
              points={normalizedPoints}
              selectedIndex={selectedImageIndex}
              expectedSegmentIds={expectedSelectedSegmentIds}
            />
          </ScoutProvider>
        )}
      </div>
    </div>
  );
}
